import type { MotorCriteriaFields } from './BrokerInsurerCommissionsPage';

/**
 * #406. CC, GVW, Age and Seating Capacity are each asked ONCE, as a From/To
 * band; an exact value is From = To. Rates saved before this still carry the
 * old single-value column (`cc`, `gvw`, ...), so the form state keeps the
 * stored columns untouched and only the DISPLAY is derived: a legacy exact
 * value shows as From = To. The engine matches `cc == X` and `X <= cc <= X`
 * identically, so the display is faithful. Saving without touching a band
 * sends the stored columns back as they were; the first edit to a band moves
 * it onto the From/To columns and clears the legacy one.
 */
export type BandAxis = 'cc' | 'gvw' | 'age' | 'seating_capacity';
export const BAND_AXES: BandAxis[] = ['cc', 'gvw', 'age', 'seating_capacity'];

type BandFields = Pick<
  MotorCriteriaFields,
  BandAxis | `${BandAxis}_from` | `${BandAxis}_to`
>;

/** What the From/To boxes of one band show. */
export function bandView(form: BandFields, axis: BandAxis): { from: string; to: string } {
  const exact = form[axis];
  if (exact !== '') return { from: exact, to: exact };
  return { from: form[`${axis}_from`], to: form[`${axis}_to`] };
}

/**
 * The form patch for typing `value` into one end of a band. While To has not
 * been edited by the user, typing in From copies into To.
 */
export function editBand(
  form: BandFields,
  axis: BandAxis,
  end: 'from' | 'to',
  value: string,
  toTouched: boolean,
): Partial<BandFields> {
  const next = { ...bandView(form, axis) };
  next[end] = value;
  if (end === 'from' && !toTouched) next.to = value;
  return { [axis]: '', [`${axis}_from`]: next.from, [`${axis}_to`]: next.to } as Partial<BandFields>;
}

/**
 * Whether To starts out as "the user's own" value. A blank or one-value band
 * keeps auto-filling; a stored real range or open end is never overwritten by
 * an edit to From.
 */
export function initialToTouched(form: BandFields): Record<BandAxis, boolean> {
  const touched = {} as Record<BandAxis, boolean>;
  for (const axis of BAND_AXES) {
    const { from, to } = bandView(form, axis);
    touched[axis] = from !== to;
  }
  return touched;
}

/**
 * The NCB select's value. A legacy rate may carry only `ncb` (an exact %): a
 * positive one is "With NCB" at that %, a zero one is "No NCB" -- which is
 * exactly how the engine matches them.
 */
export function ncbChoice(form: Pick<MotorCriteriaFields, 'ncb' | 'ncb_applicable'>): string {
  if (form.ncb_applicable) return form.ncb_applicable;
  if (form.ncb !== '') return Number(form.ncb) > 0 ? 'yes' : 'no';
  return '';
}

/** Choosing anything but "With NCB" hides the NCB % box and clears it. */
export function editNcbChoice(value: string): Partial<Pick<MotorCriteriaFields, 'ncb' | 'ncb_applicable'>> {
  return value === 'yes' ? { ncb_applicable: value } : { ncb_applicable: value, ncb: '' };
}
